import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import "react-toastify/dist/ReactToastify.css";
import {
    Col,
    Input,
    Label,
    Nav,
    NavItem,
    NavLink,
    Row,
    Spinner,
    TabContent,
    TabPane,
} from "reactstrap";
import * as Yup from "yup";
import { toast } from "react-toastify";
//
import { ICategory, ICategoryPayload, ICategoryTranslationPayload } from "api/types/_category";
import categoryApi from "api/categoryApi";
import { CONFIG_OPTION_TOAST_ERROR } from "common/toast";

export interface Props {
    item?: ICategory | null;
    triggerRefresh?: () => void;
}

const CategoryForm = ({ item, triggerRefresh }: Props) => {
    const { t } = useTranslation();
    const DEFAULT_LANG = {
        CODE_EN: "en",
        CODE_KO: "ko",
    };
    const OPTION_LANG = [DEFAULT_LANG.CODE_KO, DEFAULT_LANG.CODE_EN];
    const [isLoading, setIsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(DEFAULT_LANG.CODE_KO);

    const translationSchema = Yup.object({
        lang: Yup.string().oneOf(OPTION_LANG, "Invalid language"),
        content: Yup.string().required(t("Title is required")),
        description: Yup.string().notRequired(),
    });

    const schema = Yup.object({
        title: Yup.object({
            translations: Yup.array().of(translationSchema).min(1),
        }),
    });

    const initialValues: ICategoryPayload = {
        title: {
            translations: OPTION_LANG.map((lang) => ({
                lang,
                content: ""
            })),
        },
    };

    const formik = useFormik({
        initialValues,
        validationSchema: schema,
        validateOnMount: true,
        onSubmit: async (values) => {
            try {
                setIsLoading(true);
                const response = item?.id
                    ? await categoryApi.updateCategory(item.id, values)
                    : await categoryApi.createCategory(values);
                if (typeof response === "string") {
                    toast(response, CONFIG_OPTION_TOAST_ERROR);
                    return;
                }

                if (response.statusCode !== 200) {
                    toast(response.message || "Error", CONFIG_OPTION_TOAST_ERROR);
                    return;
                }
                triggerRefresh && triggerRefresh();
            } catch (err) {
                setIsLoading(false);
                console.error(err);
            }
        },
    });

    const getTranslationError = (index: number, field: keyof ICategoryTranslationPayload) => {
        const errors = formik.errors.title?.translations;
        const item = errors?.[index];
        if (item && typeof item !== "string") {
            return item[field];
        }
        return undefined;
    };

    const handleTranslationChange = (lang: string, field: string, value: string) => {
        const updatedTranslations = formik.values.title.translations.map(t =>
            t.lang === lang ? { ...t, [field]: value } : t
        );
        formik.setFieldValue("title.translations", updatedTranslations);
    };

    useEffect(() => {
        if (!!item?.translations?.length) {
            const translations = OPTION_LANG.map((lang) => {
                const data = item.translations.find((t) => t.lang === lang);
                return {
                    lang,
                    content: data?.title || "",
                };
            });
            formik.setValues({ title: { translations } });
        }
    }, [item]);

    return (
        <div className="mt-3 position-relative">
            <form onSubmit={formik.handleSubmit}>
                {/* Tabs */}
                <div style={{ background: "#f3f6f9", borderTopLeftRadius: "0.6rem", borderTopRightRadius: "0.6rem", width: "fit-content" }}>
                    <Nav tabs className="nav nav-tabs nav-link-none-hover nav-justified">
                        {OPTION_LANG.map((lang) => (
                            <NavItem key={lang} style={{ minWidth: "180px" }}>
                                <NavLink
                                    style={{
                                        cursor: "pointer",
                                        background: activeTab === lang ? "var(--vz-primary)" : "unset",
                                        color: activeTab === lang ? "white" : "unset",
                                        borderTopLeftRadius: "0.6rem",
                                        borderTopRightRadius: "0.6rem",
                                    }}
                                    onClick={() => setActiveTab(lang)}
                                >
                                    {lang === DEFAULT_LANG.CODE_KO ? "한국어" : "English"}
                                </NavLink>
                            </NavItem>
                        ))}
                    </Nav>
                </div>

                {/* Tab Content */}
                <TabContent
                    activeTab={activeTab}
                    style={{
                        border: "1px dashed var(--vz-primary)",
                        borderTop: "1px solid #e9ebec",
                        padding: "10px",
                        borderBottomLeftRadius: "4px",
                        borderBottomRightRadius: "4px",
                    }}
                >
                    {formik.values.title.translations.map((iItem, index) => (
                        <TabPane key={iItem.lang} tabId={iItem.lang} className="position-relative">
                            <Row>
                                <Col lg={12}>
                                    <div className="mt-3 mb-3">
                                        <Label className="form-label">
                                            {iItem.lang === DEFAULT_LANG.CODE_KO ? t("Category Name") : t("Category name")}
                                            <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            type="text"
                                            placeholder={iItem.lang === DEFAULT_LANG.CODE_KO ? "카테고리 이름을 입력하세요" : "Enter the category name"}
                                            value={iItem.content}
                                            onChange={(e) => handleTranslationChange(iItem.lang, "content", e.target.value)}
                                            maxLength={100}
                                        />
                                        {formik.touched.title?.translations?.[index] && getTranslationError(index, "content") && (
                                            <div className="text-danger mt-2">{getTranslationError(index, "content")}</div>
                                        )}
                                    </div>
                                </Col>
                            </Row>
                        </TabPane>
                    ))}
                </TabContent>

                <div className="hstack gap-2 justify-content-end mt-5">
                    <button type="submit" disabled={!formik.dirty || !formik.isValid || isLoading} className="btn btn-primary fs-14">
                        {isLoading ? (
                            <Spinner size="sm" className="me-2" />
                        ) : !!item?.id ? (
                            <i className="ri-login-circle-line align-bottom me-2" />
                        ) : (
                            <i className="ri-add-fill align-bottom me-2" />
                        )}
                        {item?.id ? t("Button Update Category") : t("Button Create Category")}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CategoryForm;
